import type { ResourceLanguage } from "../adapters/i18next";
import type { SupportedLanguage } from "../config/languages";
import type { Namespace } from "../config/translations";
import {
  INITIAL_TRANSLATION_RESOURCES,
  NAMESPACES,
} from "../config/translations";

export type TranslationResult = {
  data: Record<string, unknown> | null;
  error: Error | null;
};

export type LanguageLoaders = Record<
  string,
  /**
   * NOTE: adapterに依存しているが、ResourceLanguageの型定義自体は以下の通り簡素なためadapterから型変換せずに使用
   * [namespace: string]:string | {
   *     [key: string]: any;
   * }
   */
  () => Promise<{ default: ResourceLanguage }>
>;

export type DynamicImportLoader = Record<
  Exclude<SupportedLanguage, "en-US">,
  LanguageLoaders
>;

const TRANSLATION_LOADERS: DynamicImportLoader = {
  "ja-JP": {
    [NAMESPACES.DEFAULT]: () => import("../translations/ja/default"),
    [NAMESPACES.ZOD]: () => import("../translations/ja/zod"),
  },
  "id-ID": {
    [NAMESPACES.DEFAULT]: () => import("../translations/id/default"),
    [NAMESPACES.ZOD]: () => import("../translations/id/zod"),
  },
  "vn-VN": {
    [NAMESPACES.DEFAULT]: () => import("../translations/vn/default"),
    [NAMESPACES.ZOD]: () => import("../translations/vn/zod"),
  },
  "th-TH": {
    [NAMESPACES.DEFAULT]: () => import("../translations/th/default"),
    [NAMESPACES.ZOD]: () => import("../translations/th/zod"),
  },
  "zh-CN": {
    [NAMESPACES.DEFAULT]: () => import("../translations/zhCN/default"),
    [NAMESPACES.ZOD]: () => import("../translations/zhCN/zod"),
  },
};

export interface TranslationCore {
  loadTranslation(
    language: SupportedLanguage,
    namespace: Namespace,
  ): Promise<TranslationResult>;
}

class TranslationLoader implements TranslationCore {
  constructor(private loaderMap?: DynamicImportLoader) {}

  async loadTranslation(
    language: SupportedLanguage,
    namespace: Namespace,
  ): Promise<TranslationResult> {
    try {
      if (language === "en-US") {
        return {
          data: INITIAL_TRANSLATION_RESOURCES[language][namespace],
          error: null,
        };
      }

      const loader =
        this.loaderMap?.[language]?.[namespace] ??
        TRANSLATION_LOADERS[language]?.[namespace];

      if (!loader) {
        return {
          data: INITIAL_TRANSLATION_RESOURCES["en-US"][namespace],
          error: null,
        };
      }

      const { default: translations } = await loader();
      return { data: translations, error: null };
    } catch (error) {
      console.error(
        `Failed to load translations for ${language}/${namespace}:`,
        error,
      );
      return {
        data: INITIAL_TRANSLATION_RESOURCES["en-US"][namespace],
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  }
}

export const createTranslationLoader = (
  loaderMap?: DynamicImportLoader,
): TranslationCore => new TranslationLoader(loaderMap);
